import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from "@angular/forms";
import { ModalController, Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subscription } from "rxjs";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { WeeklyPlanning } from "src/model/class/general classes/weekly-planning";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { NetworkService } from "src/service/network.service";
import { ToastService } from "src/service/toast.service";
import { ToastType } from "src/components/shared-components/enum/enumMisc";

/**
 * Modal used to create/update a single time-slice of a weekly-planning day: it lets the user pick the value (T1..T5)
 * and the start/end time of the slice, and returns the selection through the ModalController dismiss payload.
 */
@Component({
    selector: "app-day-period-change",
    templateUrl: "./day-period-change.component.html",
    styleUrls: ["./day-period-change.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DayPeriodChangeComponent
  implements OnInit, OnDestroy, iConnToolbarColour, iEvent
{
  private _lblErrorDate: string =
    "La data di inizio deve essere maggiore di quella di fine";

  private _lblhourSelectionWarning: string =
    "Questa selezione modificherà la fascia selezionata impostando il suo orario di inizio alle ore '00:00' e il suo orario di fine alle ore '24:00'. Si desidera procedere?";
  private _warning: string = "Attenzione!";
  private _networkServiceSubscription: Subscription = null;
  private _translateSubscription: Subscription;
  private _originalStartPeriod: string = "";
  private _originalEndPeriod: string = "";

  public isApp = false;
  public toolBarColor: string = "primary";
  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public _eventSubscriptions: Array<Subscription> = null;
  public periodForm: UntypedFormGroup = null;

  @Input() startPeriodIn: number;
  @Input() endPeriodIn: number;
  @Input() tValue: string;
  @Input() currentWeeklyPlanning: WeeklyPlanning;
  @Input() stringValue: any[];

  constructor(
    private _fb: UntypedFormBuilder,
    private _modalController: ModalController,
    private _platform: Platform,
    private _toastService: ToastService,
    private _translateService: TranslateService,
    public networkService: NetworkService,
    private _eventService: EventService,
    private _alertService: AlertService
  ) {
    this._platform.ready().then(async () => {
      this.isApp = this._platform.is("ios") || this._platform.is("android");
    });

    this.periodForm = this._fb.group({
      tValue: new UntypedFormControl("T1"),
      startPeriod: new UntypedFormControl("00:00"),
      endPeriod: new UntypedFormControl("00:00"),
    });
  }

  ngOnInit() {
    this.loadTranslation();
    this.subscribeEvents();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();
    this.initForm();
  }

  ngOnDestroy() {
    this._networkServiceSubscription.unsubscribe();
    this._translateSubscription.unsubscribe();
    this.unsubscribeEvents();
  }

  /**
   * Convert the "starting time" and "ending time" set through the selector into a single time interval, converting each of them
   * into an integer value (number of minutes elapsed since the start of the day), then close the modal returning the selection.
   *
   * ATTENTION: by convention, when BOTH the start and end are set to midnight ("00:00"/"24:00") the interval is interpreted as
   * start = "00:00" (0 minutes) and end = "24:00" (1440 minutes); otherwise it would be impossible to tell whether midnight means
   * "start of the day" or "end of the day".
   */
  public async onClickConfirm() {
    const startPeriodSplit = this.periodForm.value.startPeriod.split(":");
    const endPeriodSplit = this.periodForm.value.endPeriod.split(":");

    let endPeriodHourParsed: number = parseInt(endPeriodSplit[0]);
    const endPeriodMinuteParsed: number = parseInt(endPeriodSplit[1]);
    const startPeriodHourParsed: number = parseInt(startPeriodSplit[0]);
    const startPeriodMinuteParsed: number = parseInt(startPeriodSplit[1]);

    const startHour = moment(
      "2000-01-01 " + this.periodForm.value.startPeriod + ":00"
    );
    const endHour = moment(
      "2000-01-01 " + this.periodForm.value.endPeriod + ":00"
    );

    let confirmAction: boolean = true;

    const endsAtMidnight: boolean =
      (endPeriodHourParsed == 0 && endPeriodMinuteParsed == 0) ||
      (endPeriodHourParsed == 24 && endPeriodMinuteParsed == 0);
    const startsAtMidnight: boolean =
      (startPeriodHourParsed == 0 && startPeriodMinuteParsed == 0) ||
      (startPeriodHourParsed == 24 && startPeriodMinuteParsed == 0);

    if (startHour.isSameOrAfter(endHour) && !endsAtMidnight) {
      this._toastService.showToast(ToastType.ERROR, this._lblErrorDate, 250);
    } else {
      if (endsAtMidnight) {
        endPeriodHourParsed = 24;

        if (startsAtMidnight) {
          confirmAction = await this._alertService.presentAlertConfirmOrDismiss(
            this._warning,
            "",
            this._lblhourSelectionWarning
          );
        }
      }

      if (confirmAction) {
        this._modalController.dismiss({
          startPeriod: startPeriodHourParsed * 60 + startPeriodMinuteParsed,
          endPeriod: endPeriodHourParsed * 60 + endPeriodMinuteParsed,
          tValue: this.periodForm.value.tValue,
        });
      }
    }
  }

  /**
   * Close the modal returning a "delete" payload (start/end set to 0). Currently not used.
   */
  deleteSection() {
    this._modalController.dismiss({
      startPeriod: 0,
      endPeriod: 0,
      tValue: this.tValue,
    });
  }

  /**
   * Exit without confirming.
   */
  public onClickCancel() {
    this._modalController.dismiss();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.timeEndGreaterThanStart",
        "dictionary.weeklyPlanningHourSelectionWarning",
        "dictionary.attention",
      ])
      .subscribe((translation) => {
        this._lblErrorDate = translation["dictionary.timeEndGreaterThanStart"];
        this._lblhourSelectionWarning =
          translation["dictionary.weeklyPlanningHourSelectionWarning"];
        this._warning = translation["dictionary.attention"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalController.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this.networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  /**
   * Initialise the time-selection form using the input values provided to the component.
   */
  private initForm(): void {
    this._originalStartPeriod =
      Math.floor(this.startPeriodIn / 60)
        .toString()
        .padStart(2, "0") +
      ":" +
      (this.startPeriodIn % 60).toString().padStart(2, "0");

    this._originalEndPeriod =
      Math.floor(this.endPeriodIn / 60)
        .toString()
        .padStart(2, "0") +
      ":" +
      (this.endPeriodIn % 60).toString().padStart(2, "0");

    this.periodForm.patchValue(
      {
        tValue: this.tValue,
        startPeriod: this._originalStartPeriod,
        endPeriod: this._originalEndPeriod,
      },
      {
        onlySelf: true,
        emitEvent: false,
      }
    );
  }
}
